import * as React from 'react';

import * as Yup from 'yup';
import { withFormik, FormikProps, Form, Field } from 'formik';
import { compose } from 'redux';
import { RouteComponentProps } from 'react-router-dom';
import { AxiosResponse, AxiosError } from 'axios';

import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';

import * as actions from '../../modules/customer/action';
import { ICustomer } from '../../@types';
import axios from '../../common/axios-instance/axios-instance';
import withEditContainer, {
  IEditContainedProps
} from '../../common/edit-container/edit-container';
import FormikSelect from '../../common/formik/formik-select';
import EditIcons from '../../common/edit-icons/edit-icons';
import DeleteDialog from '../../common/delete-dialog/delete-dialog';
import FormikTextField from '../../common/formik/formik-text-field';

// Formik values for the form
export interface ICustomerFormValues {
  companyAddressStreet?: string;
  companyAddressCountry?: string;
  companyAddressCity?: string;
  companyAddressState?: string;
  companyAddressZip?: string;
  companyName: string;
  email: string;
  notes: string;
  phone: string;
  pocFirstName: string;
  pocLastName: string;
  resellerId: string;
  pocPosition?: string;
}

const formikEnhancer = withFormik<
  IEditContainedProps & RouteComponentProps<any>,
  ICustomerFormValues
>({
  mapPropsToValues: props => {
    const customer = props.entity as ICustomer;
    return {
      companyAddressStreet: customer.companyAddressStreet || '',
      companyAddressCity: customer.companyAddressCity || '',
      companyAddressState: customer.companyAddressState || '',
      companyAddressZip: customer.companyAddressZip || '',
      companyAddressCountry: customer.companyAddressCountry || '',
      companyName: customer.companyName || '',
      email: customer.email || '',
      notes: customer.notes || '',
      phone: customer.phone || '',
      pocFirstName: customer.pocFirstName || '',
      pocLastName: customer.pocLastName || '',
      pocPosition: customer.pocPosition || '',
      resellerId: customer.resellerId ? customer.resellerId.toString() : ''
    };
  },
  validationSchema: Yup.object().shape({
    companyAddressStreet: Yup.string().typeError(
      'Enter a valid street address'
    ),
    companyAddressCity: Yup.string().typeError('Enter a valid city'),
    companyAddressState: Yup.string().typeError('Enter a valid state/province'),
    companyAddressZip: Yup.number()
      .integer('Enter a valid ZIP or postal code')
      .typeError('Enter a valid ZIP or postal code'),
    companyAddressCountry: Yup.string().typeError('Enter a valid country'),
    companyName: Yup.string().required('Company name is required'),
    phone: Yup.number().required('You must entry a phone number'),
    email: Yup.string()
      .email('This must be an email address')
      .required('Email is required'),
    notes: Yup.string(),
    pocFirstName: Yup.string(),
    pocLastName: Yup.string(),
    pocPositionName: Yup.string(),
    resellerId: Yup.number().nullable(true)
  }),
  handleSubmit: (values, formikActions) => {
    const props = formikActions.props;
    const valuesToCustomer: ICustomer = {
      companyAddressStreet: values.companyAddressStreet!,
      companyAddressCity: values.companyAddressCity!,
      companyAddressCountry: values.companyAddressCountry!,
      companyAddressState: values.companyAddressState!,
      companyAddressZip: values.companyAddressZip!,
      companyName: values.companyName,
      createdBy: parseInt(localStorage.getItem('crntUser')!, 10),
      email: values.email,
      id: props.entity.id,
      notes: values.notes,
      phone: values.phone,
      pocFirstName: values.pocFirstName,
      pocLastName: values.pocLastName,
      pocPosition: values.pocPosition!,
      userId: null,
      resellerId: values.resellerId === '' ? null : Number(values.resellerId), // cast back to number on submit?
      createdDate: new Date(),
      updatedDate: new Date()
    };

    props.onEntitySubmit(valuesToCustomer, formikActions);
  }
});

interface IResellerOption {
  label: string;
  value: string;
}

interface ICustomerEntryState {
  resellers: IResellerOption[];
}

class CustomerEntry extends React.Component<
  FormikProps<ICustomerFormValues> & IEditContainedProps
> {
  customer: ICustomer = this.props.entity as ICustomer;

  state: ICustomerEntryState = {
    resellers: [
      {
        label: 'None',
        value: ''
      }
    ]
  };

  cancelTasks = false;

  componentWillMount = () => {
    this.updateResellersList();
  };

  componentWillUnmount = () => {
    this.cancelTasks = true;
  };

  updateResellersList = (): void | Promise<any> => {
    let url;
    // Passes undefined if undefined, but the service is written to allow this for now
    url = '/api/customer/resellers?customerId=' + this.customer.id;

    return axios
      .get<ICustomer[]>(url)
      .then((response: AxiosResponse<ICustomer[]>) => {
        const content = response.data;

        const temp: IResellerOption[] = content.map(
          c =>
            ({
              value: c.id != null ? c.id.toString() : '',
              label: c.companyName
            } as IResellerOption)
        );

        temp.unshift({
          label: 'None',
          value: ''
        });

        if (!this.cancelTasks) {
          this.setState({ resellers: temp });
        }
      })
      .catch((error: AxiosError) => {
        console.log(error.message);
      });
  };

  render(): JSX.Element {
    const {
      isSubmitting,
      onEditCancel,
      onEditStart,
      formDisabled,
      deleteFn
    } = this.props; // touched, errors

    return (
      <Form>
        <Card>
          <CardHeader
            title={
              this.customer.id ? this.customer.companyName : 'Add Customer'
            }
            action={
              <EditIcons
                formDisabled={formDisabled}
                isSubmitting={isSubmitting}
                showEdit={!!this.customer.id}
                onEditStart={onEditStart}
                onEditCancel={onEditCancel}
                showDelete={!!this.customer.id}
              />
            }
          />
          <CardContent>
            <div>
              <Field
                id="customer-company-name-input"
                name="companyName"
                label="Company Name"
                fullWidth
                disabled={formDisabled}
                component={FormikTextField}
              />
            </div>

            <h4>Reseller</h4>

            <Field
              id="customer-reseller-name-input"
              name="resellerId"
              label="Reseller Options"
              fullWidth
              disabled={formDisabled}
              component={FormikSelect}
              options={this.state.resellers}
            />

            <h4>Point of Contact</h4>

            <Grid container spacing={8}>
              <Grid item xs={6} md={4}>
                <div>
                  <Field
                    id="customer-poc-first-name-input"
                    name="pocFirstName"
                    label="First Name"
                    fullWidth
                    disabled={formDisabled}
                    component={FormikTextField}
                  />
                </div>
              </Grid>
              <Grid item xs={6} md={4}>
                <div>
                  <Field
                    id="customer-poc-last-name-input"
                    name="pocLastName"
                    label="Last Name"
                    fullWidth
                    disabled={formDisabled}
                    component={FormikTextField}
                  />
                </div>
              </Grid>
              <Grid item xs={12} md={4}>
                <div>
                  <Field
                    id="customer-poc-position-input"
                    name="pocPosition"
                    label="Position"
                    fullWidth
                    disabled={formDisabled}
                    component={FormikTextField}
                  />
                </div>
              </Grid>
            </Grid>

            <div>
              <Field
                id="customer-email-input"
                name="email"
                label="Email"
                fullWidth
                disabled={formDisabled}
                component={FormikTextField}
              />
            </div>
            <div>
              <Field
                id="customer-phone-input"
                name="phone"
                label="Phone"
                fullWidth
                disabled={formDisabled}
                component={FormikTextField}
              />
            </div>

            <h4>Address</h4>

            <div>
              <Field
                id="customer-address-street-input"
                name="companyAddressStreet"
                label="Street"
                fullWidth
                disabled={formDisabled}
                component={FormikTextField}
              />
            </div>

            <Grid container spacing={8}>
              <Grid item xs={6}>
                <div>
                  <Field
                    id="customer-address-city-input"
                    name="companyAddressCity"
                    label="City"
                    fullWidth
                    disabled={formDisabled}
                    component={FormikTextField}
                  />
                </div>
              </Grid>

              <Grid item xs={6}>
                <div>
                  <Field
                    id="customer-address-state-input"
                    name="companyAddressState"
                    label="State/Province"
                    fullWidth
                    disabled={formDisabled}
                    component={FormikTextField}
                  />
                </div>
              </Grid>
            </Grid>

            <Grid container spacing={8}>
              <Grid item xs={6}>
                <div>
                  <Field
                    id="customer-address-zip-input"
                    name="companyAddressZip"
                    label="ZIP"
                    fullWidth
                    disabled={formDisabled}
                    component={FormikTextField}
                  />
                </div>
              </Grid>

              <Grid item xs={6}>
                <div>
                  <Field
                    id="customer-address-country-input"
                    name="companyAddressCountry"
                    label="Country"
                    fullWidth
                    disabled={formDisabled}
                    component={FormikTextField}
                  />
                </div>
              </Grid>
            </Grid>

            <h4>Notes</h4>

            <div>
              <Field
                id="customer-notes-input"
                name="notes"
                fullWidth
                placeholder="Enter here"
                disabled={formDisabled}
                component={FormikTextField}
                multiline
                rows={10}
              />
            </div>
          </CardContent>
        </Card>
        <DeleteDialog
          entity={this.customer}
          title={'Delete customer?'}
          prompt={
            <div>
              <p>
                Deleting customer {this.customer.companyName} will also delete
                all of its licenses.
              </p>
            </div>
          }
          deleteEntity={deleteFn}
        />
      </Form>
    );
  }
}

const config = {
  get: actions.getCustomer,
  update: actions.updateCustomer,
  clear: actions.clearCustomer,
  deleteFn: actions.deleteCustomer,
  submit: actions.submitCustomer,
  goBackOnEditEnd: false,
  reducerName: 'customerReducer',
  entityName: 'customer',
  formikEnhancer
};

const enhance = compose(withEditContainer(config));

export default enhance(CustomerEntry);



// WEBPACK FOOTER //
// ./src/features/customer-entry/customer-entry.tsx